<script setup lang="ts">
import type { MessageSchema } from '@/i18n';
import type { Ingredient } from '@/types/dish';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, te } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const props = withDefaults(
  defineProps<{
    ingredients: Ingredient[];
    numberOfPeople?: number;
    gradientFrom: string;
    listHeight: string;
  }>(),
  {
    numberOfPeople: 1,
  },
);

function calculatePricePerPerson(ingredient: Ingredient, numberOfPeople: number): string {
  if (!ingredient.price) return '';

  const pricePerServing = ingredient.price;
  const totalPrice = pricePerServing * numberOfPeople;

  return totalPrice.toFixed(2);
}

const convertUnits = (quantity: number | undefined, unit: string): { quantity: number | undefined; unit: string } => {
  if (!quantity) return { quantity: quantity, unit: unit };

  const quantityAdjusted = quantity * props.numberOfPeople;
  if (unit === 'g' && quantityAdjusted >= 1000) {
    return { quantity: quantity / 1000, unit: 'kg' };
  } else if (unit === 'kg' && quantityAdjusted < 1) {
    return { quantity: quantity * 1000, unit: 'g' };
  }
  if (unit === 'ml' && quantityAdjusted >= 1000) {
    return { quantity: quantity / 1000, unit: 'l' };
  } else if (unit === 'ml' && quantityAdjusted >= 100) {
    return { quantity: quantity / 100, unit: 'dl' };
  } else if (unit === 'l' && quantityAdjusted < 0.1) {
    return { quantity: quantity * 1000, unit: 'ml' };
  } else if (unit === 'l' && quantityAdjusted < 1) {
    return { quantity: quantity * 10, unit: 'dl' };
  }
  return { quantity, unit };
};

const formattedIngredients = computed(() =>
  props.ingredients.map((ingredient): Ingredient => {
    const { quantity, unit } = convertUnits(ingredient.quantity, ingredient.unit);
    const translationKey = `ingredients.unit.symbol.${unit}`;
    const translatedUnit = te(translationKey) ? t(translationKey) : unit; // Utilisez te pour vérifier la clé

    return {
      ...ingredient,
      quantity: quantity ? quantity * props.numberOfPeople : undefined,
      unit: translatedUnit,
    };
  }),
);

const formatQuantity = (value) => {
  if (!value) return '';
  return Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, '');
};
</script>

<template>
  <div class="relative">
    <div class="overflow-y-scroll" :class="listHeight">
      <div
        v-for="ingredient in formattedIngredients"
        :key="ingredient.name"
        class="grid items-center gap-2 text-sm text-primary-950 last:mb-4"
        style="grid-template-columns: 3rem auto 3rem"
      >
        <div class="flex items-center gap-1 whitespace-nowrap">
          <span class="min-w-[1.5rem] text-right">{{ formatQuantity(ingredient.quantity) }}</span>
          <span class="min-w-[1rem]">{{ ingredient.unit }}</span>
        </div>
        <div class="truncate">
          <span>{{ ingredient.name }}</span>
        </div>
        <div class="text-right">
          <span>
            {{ calculatePricePerPerson(ingredient, props.numberOfPeople) }}
          </span>
        </div>
      </div>
    </div>
    <div class="absolute -bottom-1 h-8 w-full bg-gradient-to-t" :class="gradientFrom"></div>
  </div>
</template>
